import { useEffect, useRef } from "react";
import { play } from "./sound";

/**
 * Says it is your turn when you are not looking at the page.
 *
 * Two children playing on tablets at the same table do not need this. Two
 * children playing from their own houses very much do: you take your turn, put
 * the iPad down, and nothing at all tells you when the other one has moved.
 * Games were being abandoned half-played for no better reason than neither
 * child knowing it was their go.
 *
 * A tab title and a sound, and nothing else. Real notifications would need a
 * permission prompt, a push subscription and an endpoint stored per device —
 * exactly the kind of data the privacy policy promises this app does not keep,
 * for a feature that does not need it.
 */

const WAITING_TITLE = "⏰ Your turn! — PlayPal";

/**
 * What the tab was called before we interrupted, remembered only while it is
 * interrupted. Read at the moment of overwriting rather than when this module
 * loads, so it cannot capture a title that has not been set yet.
 */
let restingTitle: string | null = null;

function announce() {
	if (restingTitle === null) restingTitle = document.title;
	document.title = WAITING_TITLE;
}

function restore() {
	if (restingTitle === null) return;
	document.title = restingTitle;
	restingTitle = null;
}

export function useTurnAlert(yourTurn: boolean): void {
	const wasYourTurn = useRef(yourTurn);

	useEffect(() => {
		const becameYourTurn = yourTurn && !wasYourTurn.current;
		wasYourTurn.current = yourTurn;

		// Only when they are looking somewhere else. Retitling a tab somebody is
		// already reading is noise, and the board says whose turn it is in words.
		if (!becameYourTurn || !document.hidden) return;

		announce();
		// The audio context was unlocked by the taps that got them into the game,
		// so this survives the tab going to the background. Where a browser stops
		// it anyway, the title is the part that carries the message.
		play("join");
	}, [yourTurn]);

	// Coming back to the page is the answer to the question the title asked.
	useEffect(() => {
		const onVisible = () => {
			if (!document.hidden) restore();
		};
		document.addEventListener("visibilitychange", onVisible);
		return () => {
			document.removeEventListener("visibilitychange", onVisible);
			restore();
		};
	}, []);
}
